import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaBolt, FaLaptop, FaEye, FaAward, FaShieldAlt, FaHeadset } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const SectionWhyChoose = () => {
  const cardsRef = useRef([]);

  useEffect(() => {
    cardsRef.current.forEach((card, index) => {
      gsap.fromTo(
        card,
        { y: 40, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.6,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
          },
          delay: index * 0.1,
        }
      );
    });
  }, []);

  const features = [
    {
      icon: FaBolt,
      title: 'Quick Approval',
      description: 'Most applications are assessed on the same day.',
      color: 'from-primary to-primary-600',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
    },
    {
      icon: FaLaptop,
      title: '100% Online',
      description: 'Apply anywhere from any phone or computer.',
      color: 'from-secondary-500 to-secondary-600',
      iconBg: 'bg-secondary/20',
      iconColor: 'text-secondary-600',
    },
    {
      icon: FaEye,
      title: 'Transparent Fees',
      description: 'No hidden charges. Everything disclosed upfront.',
      color: 'from-primary-600 to-primary-700',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
    },
    {
      icon: FaAward,
      title: 'Trusted Australian Lender',
      description: 'Locally owned and operating under Australian regulation.',
      color: 'from-secondary-400 to-secondary-500',
      iconBg: 'bg-secondary/20',
      iconColor: 'text-secondary-600',
    },
    {
      icon: FaShieldAlt,
      title: 'Secure & Safe',
      description: 'Your data is protected with bank-level security.',
      color: 'from-primary to-primary-600',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
    },
    {
      icon: FaHeadset,
      title: 'Dedicated Support',
      description: 'Our friendly team is here to help every step of the way.',
      color: 'from-secondary-500 to-secondary-600',
      iconBg: 'bg-secondary/20',
      iconColor: 'text-secondary-600',
    },
  ];

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block bg-secondary/20 text-primary font-semibold px-4 py-2 rounded-full text-sm mb-4">
            Our Advantages
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark mb-4">
            Why Choose <span className="text-gradient">AIRCASH?</span>
          </h2>
          <p className="text-gray-600 text-lg">
            We make borrowing simple, transparent, and stress-free.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className="group relative bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-primary/20"
            >
              {/* Icon */}
              <div className={`w-16 h-16 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`text-2xl ${feature.iconColor}`} />
              </div>

              {/* Content */}
              <h3 className="font-display text-xl font-bold text-dark mb-3 group-hover:text-primary transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>

              {/* Hover decoration */}
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-bl-[100px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>

        {/* Trust badges 
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 flex flex-wrap justify-center items-center gap-8 md:gap-12 pt-8 border-t border-gray-100"
        >
          <div className="text-center">
            <p className="text-3xl font-display font-bold text-primary">10k+</p>
            <p className="text-sm text-gray-500">Happy Customers</p>
          </div>
          <div className="w-px h-10 bg-gray-200 hidden md:block"></div>
          <div className="text-center">
            <p className="text-3xl font-display font-bold text-primary">$50M+</p>
            <p className="text-sm text-gray-500">Loans Funded</p>
          </div>
          <div className="w-px h-10 bg-gray-200 hidden md:block"></div>
          <div className="text-center">
            <p className="text-3xl font-display font-bold text-primary">4.8★</p>
            <p className="text-sm text-gray-500">Customer Rating</p>
          </div>
          <div className="w-px h-10 bg-gray-200 hidden md:block"></div>
          <div className="text-center">
            <p className="text-3xl font-display font-bold text-primary">24h</p>
            <p className="text-sm text-gray-500">Average Approval</p>
          </div>
        </motion.div>*/}
      </div>
    </section>
  );
};

export default SectionWhyChoose;

